import "./UserKYCList.scss";

import React, { useEffect, useState } from "react";

import {
  Button,
  message,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";

import { ReloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import CustomSelect from "../../Components/CustomSelect/CustomSelect";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import { DATE_FORMAT, PAGE_LIMIT } from "../../Utils/constants";
import { showAppError } from "../../Utils/error";
import { KYCVerificationStatus } from "../../Utils/types";
import UserAPI from "../api/UserAPI";
import FIRCEligibilityList from "./components/FIRCEligibilityList";
import LUTComplianceList from "./components/LUTComplianceList";
import ReviewKYCModal from "./components/ReviewKYCModal";

const STATUS_CONTENT_MAP = {
  [KYCVerificationStatus.DETAILS_ADDED]: {
    color: "processing",
    text: "Details Added",
  },
  [KYCVerificationStatus.UNDER_REVIEW]: {
    color: "warning",
    text: "Under Review",
  },
  [KYCVerificationStatus.VERIFIED]: {
    color: "success",
    text: "Verified",
  },
  [KYCVerificationStatus.REJECTED]: {
    color: "error",
    text: "Rejected",
  },
};

const initialState = {
  loading: false,
  list: [],
  page: 1,
  limit: PAGE_LIMIT,
};

const initialFilters = {
  search: "",
  status: KYCVerificationStatus.UNDER_REVIEW, // 'details_added', 'under_review', 'verified', 'rejected'
};

const UserKYCList = () => {
  const [state, setState] = useState(initialState);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [isToggling, setIsToggling] = useState(false);

  const onChangeFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchUserKYCList = async () => {
    const newState = { ...initialState, ...state };

    newState.loading = true;

    setState({ ...newState });

    try {
      const resp = await UserAPI.getUserKYCList({
        page: state.page,
        limit: state.limit,
        search: filters.search,
        status: filters.status,
      });
      if (resp?.code === 200) {
        newState.list = [...(resp.result || [])].map((item) => ({
          ...item,
          key: item._id,
        }));
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      newState.loading = false;
      setState({ ...newState });
    }
  };

  const reloadUserKYCList = () => {
    setState((prev) => ({ ...prev, list: [] }));
    fetchUserKYCList();
  };

  const toggleAutomaticWithdrawals = async (userId, currentStatus) => {
    setIsToggling(true);
    try {
      const resp = await UserAPI.toggleAutomaticWithdrawals(
        userId,
        !currentStatus
      );
      if (resp?.code === 200) {
        message.success(
          `Automatic withdrawals ${
            !currentStatus ? "enabled" : "disabled"
          } successfully`
        );
        reloadUserKYCList();
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsToggling(false);
    }
  };

  useEffect(() => {
    reloadUserKYCList();
  }, [state.page, state.limit, filters]);

  useEffect(() => {
    setSelectedKYC(null);
  }, []);

  const columns = [
    {
      title: "Submitted On",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => moment(date || new Date()).format(DATE_FORMAT),
    },
    {
      title: "User Info",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <TableUserInfoItem
          name={record?.name}
          profilePic={record?.profilePicUrl}
          email={record?.email}
          phone={record?.phone}
          country={record?.country}
          dialCode={record?.dialCode}
          isCopyable
        />
      ),
    },
    {
      title: "Authority",
      dataIndex: "authority",
      key: "authority",
      render: (authority) => authority.toTitleCase(),
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
    },
    {
      title: "Bank Details",
      dataIndex: "creator",
      key: "creator",
      render: (_, record) => {
        const bankName =
          record?.bankName || record?.internationalBankAccount?.bank_name;
        const accountName =
          record?.accountName ||
          record?.internationalBankAccount?.account_holder_name;
        const accountNumber =
          record?.accountNumber ||
          record?.internationalBankAccount?.account_number;
        const ifsc =
          record?.ifsc || record?.internationalBankAccount?.routing_number;

        return (
          <Space direction="vertical" size={8} style={{ width: 250 }}>
            <span>
              <strong>Account Name:</strong> {accountName}{" "}
            </span>
            <span>
              <strong>Account Number:</strong> {accountNumber}{" "}
            </span>
            <span>
              <strong>Bank Name: </strong>
              {bankName}
            </span>
            <span>
              <strong>IFSC:</strong> {ifsc}
            </span>
          </Space>
        );
      },
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (_, record) => (
        <div style={{ width: 150 }}>
          <Typography.Paragraph
            ellipsis={{ rows: 2, expandable: true, symbol: "Read more" }}
            style={{
              marginBottom: 8,
            }}
          >
            {record?.address}
          </Typography.Paragraph>
          PIN: {record?.pincode}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "verificationStatus",
      key: "verificationStatus",
      render: (verificationStatus) => (
        <Tag color={STATUS_CONTENT_MAP?.[verificationStatus]?.color}>
          {STATUS_CONTENT_MAP?.[verificationStatus]?.text}
        </Tag>
      ),
    },
    {
      title: "Auto Withdrawals",
      dataIndex: "enableAutomaticWithdrawals",
      key: "enableAutomaticWithdrawals",
      render: (enabled, record) => {
        if (record?.verificationStatus !== KYCVerificationStatus.VERIFIED) {
          return "-";
        }
        return (
          <Tag color={enabled ? "success" : "default"}>
            {enabled ? "Enabled" : "Disabled"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => {
        return (
          <Space direction="vertical" size={8}>
            <Button
              className="siteBtn accentBtnV1"
              onClick={() => setSelectedKYC(record)}
              block
            >
              {record?.verificationStatus === KYCVerificationStatus.UNDER_REVIEW
                ? "Review"
                : "View documents"}
            </Button>
            {record?.verificationStatus === KYCVerificationStatus.VERIFIED && (
              <Button
                className="siteBtn accentBtnV1"
                onClick={() =>
                  toggleAutomaticWithdrawals(
                    record?._id,
                    record?.enableAutomaticWithdrawals
                  )
                }
                type={record?.enableAutomaticWithdrawals ? "danger" : "primary"}
                loading={isToggling}
                block
              >
                {record?.enableAutomaticWithdrawals ? "Disable" : "Enable"}
                {" Auto Withdrawals"}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div className="kyc-list">
        <Typography.Title
          level={4}
          className="kyc-list__title"
          style={{
            marginBottom: 0,
          }}
        >
          Manage KYC
        </Typography.Title>

        <Tabs defaultActiveKey="kyc" destroyInactiveTabPane>
          <Tabs.TabPane tab="KYC Verification" key="kyc">
            <div
              className="filters-wrapper kyc-list__filters"
              style={{
                paddingBlock: 16,
                paddingTop: 0,
                paddingBottom: 0,
              }}
            >
              <CustomSearchbar
                placeholder="Search by name, email or phone"
                searchQuery={filters.search}
                setSearchQuery={(value) => onChangeFilters("search", value)}
                style={{ marginBottom: 0, maxWidth: 500 }}
              />

              <Tooltip title="Refresh">
                <Button size="large" onClick={reloadUserKYCList}>
                  <ReloadOutlined />
                </Button>
              </Tooltip>

              <CustomSelect
                // remove details_added from the list
                itemsList={Object.values(KYCVerificationStatus)
                  .filter(
                    (item) => item !== KYCVerificationStatus.DETAILS_ADDED
                  )
                  .map((value) => ({
                    _id: value,
                    // replace - with space and then capitalize first letter
                    title: value.replace(/_/g, " ").toTitleCase(),
                  }))}
                item={filters.status}
                placeholder="Select KYC status"
                onSelectItem={(value) => onChangeFilters("status", value)}
                hasSelectAll={false}
                style={{
                  width: 250,
                  marginLeft: "auto",
                }}
              />
            </div>

            <Table
              key="userKYCList"
              columns={columns}
              dataSource={state.loading ? [] : state.list}
              pagination={{
                current: state.page,
                // total: state.total,
                showSizeChanger: false,
                pageSize: state.limit,
                onChange: (p) => setState((prev) => ({ ...prev, page: p })),
                // onShowSizeChange: (_, l) =>
                //   setState((prev) => ({ ...prev, limit: l })),
              }}
              loading={state.loading}
              rowKey={(record) => record._id}
              locale={{
                emptyText:
                  (state.loading && "Loading...") ||
                  ((filters.search || filters.status) &&
                    "Sorry, we didn't find any matching results") ||
                  "No KYC submissions found",
              }}
              // expandable={{
              //   expandedRowRender: (record) => (
              //     <CreatorDetailExpand creatorId={record?._id} />
              //   ),
              // }}
              scroll={{ x: "auto" }}
            />
          </Tabs.TabPane>

          {/* LUT / FIRC export-earnings compliance (TMG-2249) */}
          <Tabs.TabPane tab="LUT / FIRC Compliance" key="lut">
            <Tabs defaultActiveKey="eligibility" destroyInactiveTabPane>
              {/* enable the FIRC gate for a creator so they can submit their LUT */}
              <Tabs.TabPane tab="FIRC Eligibility" key="eligibility">
                <FIRCEligibilityList />
              </Tabs.TabPane>
              {/* review + verify/reject submitted LUTs */}
              <Tabs.TabPane tab="LUT Reviews" key="reviews">
                <LUTComplianceList />
              </Tabs.TabPane>
            </Tabs>
          </Tabs.TabPane>
        </Tabs>
      </div>

      {selectedKYC ? (
        <ReviewKYCModal
          kyc={selectedKYC}
          setKyc={setSelectedKYC}
          isModalOpen={Boolean(selectedKYC)}
          setIsModalOpen={(isOpen) =>
            setSelectedKYC(isOpen ? selectedKYC : null)
          }
          reloadUserKYCList={(status) => onChangeFilters("status", status)}
        />
      ) : null}
    </>
  );
};

export default UserKYCList;
